import { useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/auth';
import { useAuthorization } from '@/authorization';
import {
  IdentityContext,
  type AuditEvent,
  type IdentityContextValue,
} from '@/iam/context/IdentityContext';
import { allIdentityPermissions } from '@/iam/services';
import {
  publishPlatformEvent,
  toPlatformActor,
  type PlatformEventCategory,
  type PlatformEventSeverity,
} from '@/platform/events';

interface IdentityProviderProps {
  children: ReactNode;
}

function resolveEventCategory(type: AuditEvent['type']): PlatformEventCategory {
  if (type.includes('PERMISSION') || type.includes('ROLE')) {
    return 'authorization';
  }

  if (
    type.includes('ACCOUNT') ||
    type.includes('LOGIN') ||
    type.includes('LOGOUT')
  ) {
    return 'authentication';
  }

  if (
    type.includes('GROUP') ||
    type.includes('USER') ||
    type.includes('INVITATION')
  ) {
    return 'administration';
  }

  return 'system';
}

function resolveEventSeverity(type: AuditEvent['type']): PlatformEventSeverity {
  if (type === 'GROUP_DELETED' || type === 'PERMISSION_REVOKED') {
    return 'high';
  }

  if (type === 'ACCOUNT_LOCKED') {
    return 'critical';
  }

  return 'medium';
}

function resolveResource(type: AuditEvent['type']): string {
  if (
    type.includes('USER') ||
    type === 'INVITATION_SENT' ||
    type.includes('ACCOUNT')
  ) {
    return 'users';
  }

  if (type.includes('GROUP')) {
    return 'groups';
  }

  if (type.includes('ROLE') || type.includes('PERMISSION')) {
    return 'roles';
  }

  return 'systemSettings';
}

export function IdentityProvider({ children }: IdentityProviderProps) {
  const { user } = useAuth();
  const { hasPermission } = useAuthorization();
  const [recentAuditEvents, setRecentAuditEvents] = useState<AuditEvent[]>([]);

  const permissions = useMemo(() => {
    return new Set(
      allIdentityPermissions.filter((permission) => hasPermission(permission)),
    );
  }, [hasPermission]);

  const value: IdentityContextValue = useMemo(
    () => ({
      permissions,
      hasPermission: (permission) => permissions.has(permission),
      emitAuditEvent: (event) => {
        const occurredAt = new Date().toISOString();
        const actor = toPlatformActor(user, 'System Process');

        publishPlatformEvent({
          type: event.type,
          category: resolveEventCategory(event.type),
          actor,
          resource: resolveResource(event.type),
          resourceId: event.targetId,
          details: `${event.type.replace(/_/g, ' ')} executed for ${event.targetId}.`,
          severity: resolveEventSeverity(event.type),
          environment: import.meta.env.MODE ?? 'local',
          product: 'Global Portal',
          ipAddress: '127.0.0.1',
          metadata: event.metadata,
        });

        setRecentAuditEvents((current) =>
          [
            {
              ...event,
              id: `${event.type}-${event.targetId}-${Date.now()}`,
              occurredAt,
            },
            ...current,
          ].slice(0, 200),
        );
      },
      recentAuditEvents,
    }),
    [permissions, recentAuditEvents, user],
  );

  return (
    <IdentityContext.Provider value={value}>
      {children}
    </IdentityContext.Provider>
  );
}
